import { BigFormRow, MediumFormRow, FormSection } from 'framework/ui/layout';
import { useEffect } from 'react';
import {
  Dropdown,
  TextInput,
  FormFieldsData,
  useFormContext,
  ZoneDateTimeInput,
  ZonedApiDateFormat,
  ClearAfterUnmountWrapper,
} from 'framework/form';
import additionalActivitiesFormValidationSchema from './additionalActivitiesFormValidationSchema';
import { EmployeeInput } from 'domain/shared/employee';
import { CandidateResumeUploaderInput } from 'domain/shared/candidate';
import { SubmitButton } from 'framework/ui/ui-builder';
import { Button } from 'framework/ui/mui-builder';
import { Box } from '@mui/material';

const ACTIVITY_TYPE = {
  additionalInterview: 'additionalInterview',
  referenceCheck: 'referenceCheck',
  additionalMaterials: 'additionalMaterials',
  testTask: 'testTask',
};

interface AdditionalActivitiesFormProps {
  handleCancelClick: () => void;
}

// TODO: change Select to ui kit select
const AdditionalActivitiesForm = ({ handleCancelClick }: AdditionalActivitiesFormProps) => {
  const { submitForm, isSubmitEnabled, isLoadingArtifact, onChangeCallback, setValidationSchemaAction } =
    useFormContext();

  const onActivityTypeChange = ({ activityType }: FormFieldsData) => {
    if (onChangeCallback) {
      onChangeCallback({
        activityType,
        isInterviewRequired: activityType === ACTIVITY_TYPE.additionalInterview,
        isReferenceCheckRequired: activityType === ACTIVITY_TYPE.referenceCheck,
        isTestTaskRequired: activityType === ACTIVITY_TYPE.testTask,
        isAdditionalMaterialRequired: activityType === ACTIVITY_TYPE.additionalMaterials,
      });
    }
  };

  useEffect(() => {
    if (setValidationSchemaAction) {
      setValidationSchemaAction(additionalActivitiesFormValidationSchema);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FormSection>
        <MediumFormRow>
          <Dropdown
            source="activityType"
            label="Activity type"
            options={[
              { value: ACTIVITY_TYPE.additionalInterview, label: 'Additional Interview' },
              { value: ACTIVITY_TYPE.referenceCheck, label: 'Reference Check' },
              { value: ACTIVITY_TYPE.additionalMaterials, label: 'Additional Materials' },
              { value: ACTIVITY_TYPE.testTask, label: 'Test Task' },
            ]}
            isRequired
            onChangeCallback={onActivityTypeChange}
          />
        </MediumFormRow>

        <MediumFormRow>
          <ClearAfterUnmountWrapper isVisible={formData => formData.activityType === 'additionalInterview'}>
            <ZoneDateTimeInput
              label="Interview date & time"
              source="interviewDateTime"
              isRequired
              apiDateFormat={ZonedApiDateFormat.ISO_8601_DATETIME_WITH_OFFSET}
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={formData => formData.activityType === 'additionalInterview'}>
            <Dropdown
              source="interviewType"
              label="Interview type"
              options={[
                { value: 'ROLE_RELATED', label: 'Role-related' },
                { value: 'GENERAL', label: 'General' },
              ]}
              isRequired
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={formData => formData.activityType === 'additionalInterview'}>
            <EmployeeInput label="Interviewer" source="interviewerAlias" isRequired />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={formData => formData.activityType === 'additionalInterview'}>
            <Dropdown
              label="Interview method"
              source="interviewMethod"
              options={[
                { value: 'online', label: 'Online' },
                { value: 'offline', label: 'Offline' },
              ]}
              isRequired
            />
          </ClearAfterUnmountWrapper>
        </MediumFormRow>
        <BigFormRow>
          <ClearAfterUnmountWrapper
            isVisible={formData =>
              formData.activityType === 'additionalInterview' && formData.interviewType === 'ROLE_RELATED'
            }
          >
            <CandidateResumeUploaderInput label="Candidate's CV" source="resumeId" maxElements={1} isRequired />
          </ClearAfterUnmountWrapper>
        </BigFormRow>

        <BigFormRow>
          <ClearAfterUnmountWrapper
            isVisible={formData =>
              formData.activityType === 'additionalInterview' && formData.interviewType === 'ROLE_RELATED'
            }
          >
            <CandidateResumeUploaderInput label="Screening materials" source="screeningMaterialsId" maxElements={1} />
          </ClearAfterUnmountWrapper>
        </BigFormRow>
        <BigFormRow>
          <ClearAfterUnmountWrapper isVisible={formData => formData.activityType === 'referenceCheck'}>
            <TextInput
              label="Reference details"
              source="referenceCheckDetails"
              isRequired
              placeholder="Add reference details"
            />
          </ClearAfterUnmountWrapper>
        </BigFormRow>

        <BigFormRow>
          <ClearAfterUnmountWrapper isVisible={formData => formData.activityType === 'additionalMaterials'}>
            <TextInput
              label="Material details"
              source="additionalMaterialDetails"
              isRequired
              placeholder="Add details about materials"
            />
          </ClearAfterUnmountWrapper>
        </BigFormRow>

        <BigFormRow>
          <ClearAfterUnmountWrapper isVisible={formData => formData.activityType === 'testTask'}>
            <TextInput
              label="Test task details"
              source="testTaskDetails"
              isRequired
              placeholder="Add details about the test task"
            />
          </ClearAfterUnmountWrapper>
        </BigFormRow>
      </FormSection>

      <Box display="flex" gap={1.25}>
        <SubmitButton
          key="submit-modal-btn"
          onClick={submitForm}
          isLoading={!isSubmitEnabled}
          disabled={isLoadingArtifact}
        />
        <Button
          variant="outlined"
          key="cancel-modal-btn"
          onClick={handleCancelClick}
          disabled={!isSubmitEnabled || isLoadingArtifact}
        >
          Cancel
        </Button>
      </Box>
    </>
  );
};

export default AdditionalActivitiesForm;
